/**
 * @fileoverview Top-level renderer entry point.
 * @module @firesquirrel/inspection-renderer
 *
 * Responsibilities:
 * - Compose every Phase 1-5 piece into the canonical entry point that
 *   consuming surfaces (system-web preview, tenant-web preview,
 *   tenant-web conductor, mobile-pwa conductor) mount.
 * - Subscribe to the host-injected `PersistAdapter` and re-render on
 *   changes.
 * - Drive the rules engine v2 (`evaluateFormRulesWithTrace`) on every
 *   change, producing the `ResolvedFormStateV2` map + rule trace that
 *   `<SectionRenderer>` and `<DataSlideIn>` both consume.
 * - Render standard forms via `<SectionRenderer>`, header forms via the
 *   shared `<HeaderFormRenderer>`, and violations via `<ModuleRenderer>`.
 * - Route contacts modules through the host-injected render slot.
 *
 * Dependencies:
 * - React 18 (peer).
 * - All Phase 1-5 components + adapters + contracts.
 *
 * Architecture:
 * - Stateless apart from the `currentFormId` tab and the bound
 *   subscription tick. Persisted state lives in `PersistAdapter`.
 * - Wrapped in `<RendererProviders>` so AttachmentPicker + ResolveContext
 *   are injected to every nested widget.
 * Notes:
 * - Completion and rule evaluation normalize unresolved tokens only for data-bound fields.
 *
 * @component InspectionRenderer
 *
 * @contract InspectionRendererProps
 * Purpose: prop contract for the canonical renderer entry point and host providers.
 * Fields: forms, persistence, context providers, module slot, and conductor callbacks.
 * Rules: Contacts modules require the host render slot; violations use package dispatch.
 * Sync: Persistence and host-owned services are injected.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type {
  InspectionForm,
  InspectionRule,
  ResolvedFormStateV2,
  RuleTraceEntry,
  FieldResponse,
} from '@firesquirrel/core-domain';
import {
  evaluateAllFormRulesWithTrace,
  validateInspectionForCompletion,
  type CompletionVerdict,
} from '@firesquirrel/core-domain';
import { buildCompletionValidationInput } from './validation/completionInput';
import {
  isUnansweredDataBoundValue,
  isUnresolvedDbSentinel,
  resolveFieldValueForValidation,
} from './validation/dataBoundFieldValue';
import { SectionRenderer } from './sections/SectionRenderer';
import { ModuleRenderer } from './modules/ModuleRenderer';
import { HeaderFormRenderer } from '@firesquirrel/ui-admin';
import { FormStepTabs } from './shell/FormStepTabs';
import type { InspectionVisibleFormSummary } from './shell/FormStepTabs';
import { DataSlideIn } from './data-panel/DataSlideIn';
import { RendererProviders } from './contracts/rendererContext';
import type { PersistAdapter } from './adapters/persistAdapter';
import type { AttachmentPicker } from './contracts/attachmentPicker';
import type { ResolveContext } from './contracts/resolveContext';
import type {
  InspectionAuditEventHandler,
  InspectionTelemetryEventHandler,
  ThumbnailResolver,
  CodeSetSearchProvider,
  ViolationPhotoProvider,
} from './contracts/rendererContext';
import type { ContactEntry } from '@firesquirrel/core-domain';
import type { ContactChange } from './violations/types';
import type { GetDensityOpts } from './tokens/densityTokens';
import { ValidationSummary } from './validation/ValidationSummary';
import type { FieldIssue } from './validation/fieldIssues';
import {
  ViolationsCaptureProvider,
  type ViolationsCaptureContextValue,
} from './violations/violationsCaptureContext';
import {
  allowsLegacyViolationFallback,
  findViolationForMapping,
} from './violations/findViolationForMapping';
import type { LoggedViolation } from './violations/types';
import {
  getPrimaryInspectorFromResponses,
  type InspectorRosterEntry,
} from './inspectors/inspectorSelection';

/**
 * @contract InspectionRendererProps
 * Purpose: Configure the shared inspection form and module renderer.
 * Fields: canonical forms, host persistence, data and attachment providers,
 * event context, validation, and read-only behavior.
 * Rules: host callbacks own durable writes; renderers remain presentation-first.
 * Sync: state changes flow through the supplied PersistAdapter.
 */
export interface InspectionRendererProps {
  mode: 'preview' | 'conduct';
  forms: InspectionForm[];
  templateRules?: readonly InspectionRule[];
  persistAdapter: PersistAdapter;
  attachmentPicker?: AttachmentPicker;
  resolveContext?: ResolveContext;
  caseId: string;
  inspectorId: string;
  /** Event-level roster used by `inspectors` widgets to select one primary. */
  inspectorRoster?: readonly InspectorRosterEntry[];
  entityName?: string;
  templateName?: string;
  currentEventIndex?: number;
  bulkPilotMode?: boolean;
  /** Render only data-bound controls as read-only without locking ordinary answers. */
  dataBoundReadOnly?: boolean;
  density?: GetDensityOpts;
  readOnly?: boolean;
  /** Hide the issue panel when the host renders its own completion summary. */
  showValidationSummary?: boolean;
  /** Initial contact pool for the contacts module. */
  contactPool?: ContactEntry[];
  /** Bubble Contact-list changes to the host (typically re-routed back to PersistAdapter). */
  onModuleContactsChange?: (contacts: ContactEntry[], changes: ContactChange[]) => void;
  onModuleViolationsChange?: (violations: LoggedViolation[]) => void;
  /** When set, renders the Data slide-in panel in a 2-column layout. */
  showDataSlideIn?: boolean;
  /** Optional reset handler shown only in preview mode. */
  onReset?: () => void;
  /**
   * Host-reported connectivity. When `true`, Camera/Photo/Signature/Location
   * widgets render their "offline" pill and disable capture controls that
   * require live network. The renderer also surfaces this via `useIsOffline()`
   * for widget-internal branches.
   */
  isOffline?: boolean;
  /** Host-injected thumbnail resolver for visual photo previews in Camera/Photo widgets. */
  thumbnailResolver?: ThumbnailResolver;
  /** Ownership of URLs the resolver returns — 'owned' = fresh per call
   *  (renderer may revoke), 'shared' = resolver-cached (default; never revoked). */
  thumbnailUrlOwnership?: 'owned' | 'shared';
  /** Dexie-backed violation index source; matching remains package-owned. */
  codeSetSearch?: CodeSetSearchProvider;
  /** Host-owned violation photo capture and thumbnail services. */
  violationPhotos?: ViolationPhotoProvider;
  /** @contract Receives explicit user actions; autosave and read-only paths never emit. */
  onInspectionAuditEvent?: InspectionAuditEventHandler;
  /** Host callback for sanitized violation occurrence lifecycle telemetry. */
  onTelemetryEvent?: InspectionTelemetryEventHandler;
  /**
   * Opaque key forwarded to every `<FieldRenderer>`. Change it after calling
   * `persistAdapter.reset()` to trigger default-value re-application on all
   * fields — matching an initial load. Omit in conduct mode.
   */
  resetKey?: string | number;
  /**
   * Bubbles the canonical completion verdict to the host so the host's
   * "Complete Inspection" control can hard-block when any validation issue
   * exists. The verdict is computed by the SINGLE shared
   * `validateInspectionForCompletion` process in `@firesquirrel/core-domain`
   * — hosts MUST NOT re-implement completion gating.
   */
  onCompletionVerdictChange?: (verdict: CompletionVerdict) => void;
  /** Show the package-owned form tab bar. */
  showFormTabs?: boolean;
  /** Controlled active form id. */
  activeFormId?: string;
  /** Called when the selected form changes. */
  onActiveFormIdChange?: (id: string) => void;
  /** Visible forms summarized from the current completion verdict. */
  onVisibleFormsChange?: (forms: InspectionVisibleFormSummary[]) => void;
  /** Optional host rendering slot for module forms. */
  renderModule?: (
    form: InspectionForm,
    context: InspectionRendererModuleContext,
  ) => ReactNode | undefined;
}

/**
 * @contract InspectionRendererModuleContext
 * Purpose: Supplies persisted module data and write callbacks to a host renderer.
 * Fields: violations, contacts, readOnly, onContactsChange, onViolationsChange.
 */
export interface InspectionRendererModuleContext {
  violations: LoggedViolation[];
  contacts: ContactEntry[];
  readOnly: boolean;
  onContactsChange: (contacts: ContactEntry[], changes: ContactChange[]) => void;
  onViolationsChange: (violations: LoggedViolation[]) => void;
}

/**
 * @component InspectionRenderer
 * Purpose: Render standard inspection forms and shared module forms.
 * Props: InspectionRendererProps.
 * Behavior: Connects fields to the host PersistAdapter and local context providers.
 * Data Source: Host-provided forms and local-first adapters.
 * Constraints: Does not fetch or persist data directly.
 * Performance: Renders the active form and subscribes to adapter changes.
 */
export function InspectionRenderer(props: InspectionRendererProps): ReactNode {
  const {
    mode, forms, templateRules, persistAdapter, attachmentPicker, resolveContext,
    caseId, inspectorId, inspectorRoster, entityName, templateName,
    currentEventIndex = 0, bulkPilotMode, dataBoundReadOnly, density, readOnly, showValidationSummary = true, contactPool,
    onModuleContactsChange, onModuleViolationsChange, showDataSlideIn, onReset, isOffline, thumbnailResolver, thumbnailUrlOwnership, codeSetSearch, violationPhotos, resetKey,
    onCompletionVerdictChange, onInspectionAuditEvent, onTelemetryEvent,
    showFormTabs = true, activeFormId, onActiveFormIdChange, onVisibleFormsChange, renderModule,
  } = props;
  const moduleCallbacksRef = useRef({
    persistAdapter,
    onModuleContactsChange,
    onModuleViolationsChange,
  });
  moduleCallbacksRef.current = {
    persistAdapter,
    onModuleContactsChange,
    onModuleViolationsChange,
  };

  // Subscribe to adapter changes to force re-renders. The adapter is
  // the source of truth; we never copy its state into local state.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    return persistAdapter.subscribe(() => setTick((t) => t + 1));
  }, [persistAdapter]);

  const initialVisibleForms = useMemo(
    () => forms.filter((f) => f.initialVisibility !== 'hidden'),
    [forms],
  );
  const [internalActiveFormId, setInternalActiveFormId] = useState<string>(initialVisibleForms[0]?._id ?? '');
  const selectedFormId = activeFormId !== undefined ? activeFormId : internalActiveFormId;
  const changeActiveFormId = useCallback((id: string) => {
    if (activeFormId === undefined) setInternalActiveFormId(id);
    onActiveFormIdChange?.(id);
  }, [activeFormId, onActiveFormIdChange]);

  const responsesByForm = useMemo(() => {
    // `tick` is read so the memo recomputes on every adapter notify.
    void tick;
    const m: Record<string, Record<string, FieldResponse>> = {};
    for (const f of forms) {
      m[f._id] = persistAdapter.getValues(f._id);
    }
    return m;
  }, [forms, persistAdapter, tick]);

  const allResponses = useMemo(() => {
    const m: Record<string, FieldResponse> = {};
    for (const f of forms) Object.assign(m, responsesByForm[f._id]);
    return m;
  }, [forms, responsesByForm]);

  const effectiveResponsesByForm = useMemo(() => {
    const m: Record<string, Record<string, FieldResponse>> = {};
    for (const form of forms) {
      const responses = responsesByForm[form._id] ?? {};
      const effective = { ...responses };
      for (const field of form.fields) {
        const existingResponse = responses[field.id];
        if (isUnresolvedDbSentinel(field, existingResponse?.value)) {
          const value = resolveFieldValueForValidation(
            field,
            existingResponse.value,
            existingResponse.isNA,
            resolveContext ?? undefined,
          );
          effective[field.id] = { ...existingResponse, value };
          continue;
        }
        if (field.type !== 'dataBound' || !isUnansweredDataBoundValue(existingResponse?.value)) continue;
        const value = resolveFieldValueForValidation(field, undefined, undefined, resolveContext ?? undefined);
        if (value !== undefined) {
          effective[field.id] = { ...existingResponse, value, isNA: false };
        }
      }
      m[form._id] = effective;
    }
    return m;
  }, [forms, responsesByForm, resolveContext]);

  // Rule evaluation uses one flat field namespace, while completion validation
  // retains the effective response map grouped by form.
  const effectiveAllResponses = useMemo(() => {
    const m: Record<string, FieldResponse> = {};
    for (const f of forms) Object.assign(m, effectiveResponsesByForm[f._id]);
    return m;
  }, [forms, effectiveResponsesByForm]);

  // Engine v2 evaluation (per-form rules + template rules + cross-form deps).
  const resolvedStates: Record<string, ResolvedFormStateV2> = useMemo(() => {
    return evaluateAllFormRulesWithTrace(forms, effectiveAllResponses, templateRules);
  }, [forms, effectiveAllResponses, templateRules]);

  // Combined trace: union of every form's trace (each is already deduped within form).
  const rulesTrace: RuleTraceEntry[] = useMemo(() => {
    const out: RuleTraceEntry[] = [];
    const seen = new Set<string>();
    for (const fs of Object.values(resolvedStates)) {
      for (const t of fs.trace ?? []) {
        if (seen.has(t.ruleId)) continue;
        seen.add(t.ruleId);
        out.push(t);
      }
    }
    return out;
  }, [resolvedStates]);

  // Sync state map for the Data slide-in (conduct only).
  const syncState = useMemo(() => {
    void tick;
    if (mode !== 'conduct') return undefined;
    const out: Record<string, ReturnType<PersistAdapter['getSyncState']>> = {};
    for (const f of forms) out[f._id] = persistAdapter.getSyncState(f._id);
    return out;
  }, [forms, mode, persistAdapter, tick]);

  const visibleForms = useMemo(
    () => initialVisibleForms.filter((f) => !resolvedStates[f._id]?.hidden),
    [initialVisibleForms, resolvedStates],
  );

  useEffect(() => {
    if (visibleForms.length > 0 && !visibleForms.some((f) => f._id === selectedFormId)) {
      changeActiveFormId(visibleForms[0]._id);
    }
  }, [changeActiveFormId, selectedFormId, visibleForms]);

  const activeForm = useMemo(
    () => visibleForms.find((f) => f._id === selectedFormId) ?? visibleForms[0],
    [visibleForms, selectedFormId],
  );

  // Stable openedAt for the Data slide-in (lives across renders).
  const openedAt = useMemo(() => new Date().toISOString(), []);

  if (!activeForm) {
    return <div role="alert" className="p-3 text-sm text-gray-600">No forms to render.</div>;
  }

  const activeResolved = resolvedStates[activeForm._id];

  // Build the field-id → field + value/isNA maps for the SectionRenderer.
  const fieldsById = useMemo(() => {
    const m: Record<string, typeof activeForm.fields[number]> = {};
    for (const fld of activeForm.fields) m[fld.id] = fld;
    return m;
  }, [activeForm]);

  const activeValuesObj = persistAdapter.getValues(activeForm._id);
  const valuesById = useMemo(() => {
    const m: Record<string, unknown> = {};
    for (const [id, fr] of Object.entries(activeValuesObj)) m[id] = fr.value;
    return m;
  }, [activeValuesObj]);
  const naById = useMemo(() => {
    const m: Record<string, boolean> = {};
    for (const [id, fr] of Object.entries(activeValuesObj)) m[id] = !!fr.isNA;
    return m;
  }, [activeValuesObj]);

  // Canonical completion verdict. Built from the SAME forms + resolved states
  // + persisted responses + logged occurrences the renderer already holds, fed
  // into the single shared `validateInspectionForCompletion` process. The host
  // consumes `verdict.canComplete` to hard-block its Complete control; it must
  // never re-derive completion gating itself (DRY / single-source-of-truth).
  const completionVerdict = useMemo<CompletionVerdict>(() => {
    void tick;
    return validateInspectionForCompletion(
      buildCompletionValidationInput(
        forms,
        resolvedStates,
        (formId) => effectiveResponsesByForm[formId] ?? {},
        persistAdapter.getViolations(),
      ),
    );
  }, [forms, resolvedStates, effectiveResponsesByForm, persistAdapter, tick]);

  useEffect(() => {
    onCompletionVerdictChange?.(completionVerdict);
  }, [completionVerdict, onCompletionVerdictChange]);

  // Per-form issue lists. Powers the active-form ValidationSummary panel AND
  // the per-tab issue-count badges. Derived directly from the completion
  // verdict's blocking issues so the on-screen issues NEVER diverge from what
  // the Complete gate enforces (single source of truth — `getFieldIssues` via
  // `validateInspectionForCompletion`, including occurrence-required issues).
  const issuesByFormId = useMemo<Record<string, FieldIssue[]>>(() => {
    const out: Record<string, FieldIssue[]> = {};
    for (const issue of completionVerdict.blocking) {
      // `formId` is optional on FieldIssue; issues without a formId cannot be grouped
      // per-form and are skipped here (they are still counted in the overall blocking list).
      if (!issue.formId) continue;
      (out[issue.formId] ?? (out[issue.formId] = [])).push(issue);
    }
    return out;
  }, [completionVerdict]);

  const activeIssues = issuesByFormId[activeForm._id] ?? [];
  const visibleFormSummaries = useMemo<InspectionVisibleFormSummary[]>(
    () => visibleForms.map((form) => {
      const issueCount = (issuesByFormId[form._id] ?? []).length;
      return {
        id: form._id,
        name: form.display?.sortLabel ?? form.name,
        issueCount,
        complete: issueCount === 0,
      };
    }),
    [visibleForms, issuesByFormId],
  );

  useEffect(() => {
    onVisibleFormsChange?.(visibleFormSummaries);
  }, [onVisibleFormsChange, visibleFormSummaries]);

  const primaryInspectorOverride = useMemo(
    () => getPrimaryInspectorFromResponses(allResponses),
    [allResponses],
  );
  const effectiveInspectorId = primaryInspectorOverride?.id ?? inspectorId;

  function onFieldChange(fieldId: string, next: unknown): void {
    if (readOnly) return;
    const current = persistAdapter.getValues(activeForm._id);
    persistAdapter.setValues(activeForm._id, {
      ...current,
      [fieldId]: { ...current[fieldId], value: next },
    });
  }
  function onToggleNA(fieldId: string, isNA: boolean): void {
    if (readOnly) return;
    const current = persistAdapter.getValues(activeForm._id);
    persistAdapter.setValues(activeForm._id, {
      ...current,
      [fieldId]: { ...current[fieldId], isNA },
    });
  }

  const contacts: ContactEntry[] = contactPool ?? [];
  const moduleReadOnly = !!readOnly || !!bulkPilotMode;
  const moduleViolations = persistAdapter.getViolations();
  const handleModuleContactsChange = useCallback((nextContacts: ContactEntry[], changes: ContactChange[]) => {
    const { persistAdapter: adapter, onModuleContactsChange: onContactsChange } = moduleCallbacksRef.current;
    for (const change of changes) adapter.addContactChange(change);
    onContactsChange?.(nextContacts, changes);
  }, []);
  const handleModuleViolationsChange = useCallback((nextViolations: LoggedViolation[]) => {
    const { persistAdapter: adapter, onModuleViolationsChange: onViolationsChange } = moduleCallbacksRef.current;
    const existingViolations = adapter.getViolations().filter((v) => v.source === 'additional');
    const existingById = new Map(existingViolations.map((violation) => [violation._id, violation]));
    const existingIds = new Set(existingById.keys());
    const nextIds = new Set(nextViolations.map((v) => v._id));
    for (const violation of nextViolations) {
      const existing = existingById.get(violation._id);
      if (!existing) adapter.addViolation(violation);
      else if (JSON.stringify(existing) !== JSON.stringify(violation)) {
        adapter.updateViolation(violation._id, violation);
      }
    }
    for (const id of existingIds) {
      if (!nextIds.has(id)) adapter.removeViolation(id);
    }
    onViolationsChange?.(nextViolations);
  }, []);
  const moduleContext: InspectionRendererModuleContext = {
    violations: moduleViolations,
    contacts,
    readOnly: moduleReadOnly,
    onContactsChange: handleModuleContactsChange,
    onViolationsChange: handleModuleViolationsChange,
  };
  const renderedModule = activeForm.formKind === 'module'
    ? renderModule?.(activeForm, moduleContext)
    : undefined;

  // ── Bound-mapping counts per fieldId across ALL forms. Drives whether the
  //    legacy violation fallbacks (code-only, codeless question) are
  //    unambiguous: a count > 1 means two bound mappings could claim the same
  //    legacy record, so fallbacks are disabled (the safe direction when a
  //    fieldId appears in more than one form).
  const boundMappingCountByFieldId = useMemo(() => {
    const counts = new Map<string, number>();
    for (const f of forms) {
      for (const field of f.fields ?? []) {
        const bound = (field.violationMappings ?? [])
          .filter((m) => m.bindingMode !== 'pending-tenant-bind').length;
        if (bound > 0) counts.set(field.id, (counts.get(field.id) ?? 0) + bound);
      }
    }
    return counts;
  }, [forms]);

  // ── Inline violation-capture context. Wires the PersistAdapter into the
  //    new `<FieldViolationCaptures>` component that `<FieldRenderer>` mounts
  //    below every widget with `violationMappings`. The lookup is a strict
  //    priority over the field's violations: exact code+codeSetId match first,
  //    then — only when `allowsLegacyViolationFallback` says the mapping is
  //    the field's sole owner — legacy code-only and codeless question
  //    fallbacks (see findViolationForMapping).
  const violationsCaptureValue: ViolationsCaptureContextValue = useMemo(() => {
    void tick; // re-derive on adapter notifies so existing violations refresh
    const all = persistAdapter.getViolations();
    return {
      violations: all,
      inspectorId: effectiveInspectorId,
      eventIndex: currentEventIndex,
      readOnly: !!readOnly || !!bulkPilotMode,
      getViolation(fieldId, mapping) {
        return findViolationForMapping(
          all,
          fieldId,
          mapping,
          allowsLegacyViolationFallback(mapping, boundMappingCountByFieldId.get(fieldId) ?? 0),
        );
      },
      setViolation(fieldId, mapping, next) {
        const existing = findViolationForMapping(
          persistAdapter.getViolations(),
          fieldId,
          mapping,
          allowsLegacyViolationFallback(mapping, boundMappingCountByFieldId.get(fieldId) ?? 0),
        );
        if (next === null) {
          if (existing) persistAdapter.removeViolation(existing._id);
          return;
        }
        if (existing) {
          persistAdapter.updateViolation(existing._id, next as Partial<LoggedViolation>);
        } else {
          persistAdapter.addViolation(next);
        }
      },
    };
  }, [persistAdapter, effectiveInspectorId, currentEventIndex, readOnly, bulkPilotMode, boundMappingCountByFieldId, tick]);

  return (
    <RendererProviders
      attachmentPicker={attachmentPicker}
      resolveContext={resolveContext}
      isOffline={isOffline}
      inspectorRoster={inspectorRoster}
      thumbnailResolver={thumbnailResolver}
      thumbnailUrlOwnership={thumbnailUrlOwnership}
      codeSetSearch={codeSetSearch}
      violationPhotos={violationPhotos}
      onInspectionAuditEvent={onInspectionAuditEvent}
      onTelemetryEvent={onTelemetryEvent}
      inspectionAuditContext={{ caseId, eventIndex: currentEventIndex, readOnly: !!readOnly || !!bulkPilotMode }}
    >
     <ViolationsCaptureProvider value={violationsCaptureValue}>
      <div data-inspection-renderer data-mode={mode} className={showDataSlideIn ? 'grid grid-cols-[1fr_320px] h-full' : 'h-full'}>
        <div className="flex flex-col h-full overflow-y-auto">
          {/* Form tab bar — each tab carries an issue-count badge driven by
              the same `getFieldIssues` predicate the per-widget wrappers use,
              so the badge stays in lockstep with what the inspector actually
              sees on screen. */}
          {showFormTabs && visibleFormSummaries.length > 1 && (
            <FormStepTabs
              forms={visibleFormSummaries}
              activeFormId={activeForm._id}
              onActiveFormIdChange={changeActiveFormId}
              showIssueCounts={!readOnly && (mode === 'conduct' || mode === 'preview')}
            />
          )}

          <main className="flex-1 p-3" data-active-form-id={activeForm._id}>
            {showValidationSummary && (mode === 'conduct' || mode === 'preview') && !readOnly && activeForm.formKind !== 'module' && (
              <ValidationSummary issues={activeIssues} />
            )}
            {activeForm.formKind === 'module' ? (
              renderedModule ?? (
                activeForm.moduleConfig?.moduleType === 'contacts' ? (
                  <div role="alert" data-module-error="contacts-slot-missing" className="border border-red-200 bg-red-50 rounded-sm p-2 text-xs text-red-700">
                    Contacts module renderer is unavailable in this surface.
                  </div>
                ) : (
                  <ModuleRenderer
                    form={activeForm}
                    violations={moduleViolations}
                    readOnly={moduleReadOnly}
                    onViolationsChange={handleModuleViolationsChange}
                    inspector={{
                      userId: effectiveInspectorId,
                      userName: inspectorRoster?.find((entry) => entry.id === effectiveInspectorId)?.name ?? effectiveInspectorId,
                    }}
                    caseId={caseId}
                    currentEventIndex={currentEventIndex}
                  />
                )
              )
            ) : activeForm.formKind === 'header' ? (
              <HeaderFormRenderer
                html={activeForm.description}
                emptyText="No pre-inspection instructions have been authored for this section."
              />
            ) : (
              activeForm.layout.sections.map((section, index) => (
                <SectionRenderer
                  key={`${activeForm._id}:${section.id || 'section'}:${index}`}
                  formId={activeForm._id}
                  section={section}
                  fieldsById={fieldsById}
                  valuesById={valuesById}
                  naById={naById}
                  resolvedFields={activeResolved?.fields}
                  resolvedSection={activeResolved?.sections?.[section.id]}
                  showRequiredProgress={!readOnly && (mode === 'conduct' || mode === 'preview')}
                  bulkPilotMode={bulkPilotMode}
                  readOnly={!!readOnly}
                  dataBoundReadOnly={!!dataBoundReadOnly || mode === 'preview'}
                  density={density}
                  onFieldChange={onFieldChange}
                  onToggleNA={onToggleNA}
                  resetKey={resetKey}
                />
              ))
            )}
          </main>
        </div>

        {showDataSlideIn && (
          <DataSlideIn
            mode={mode}
            caseId={caseId}
            inspectorId={effectiveInspectorId}
            entityName={entityName}
            templateName={templateName}
            currentEventIndex={currentEventIndex}
            openedAt={openedAt}
            forms={visibleForms}
            resolvedStates={resolvedStates}
            rulesTrace={rulesTrace}
            responses={persistAdapter.getCanonicalResponses()}
            violations={persistAdapter.getViolations()}
            contactChanges={persistAdapter.getContactChanges()}
            syncState={syncState}
            onReset={mode === 'preview' ? (onReset ?? (() => persistAdapter.reset())) : undefined}
          />
        )}
      </div>
     </ViolationsCaptureProvider>
    </RendererProviders>
  );
}
